/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ChangeDetectionStrategy
} from '@angular/core';
import {PartNumberChangesAdditionalFullLotNumberModel} from '../../models/part-number-changes-additional-full-lot-number.model';
import { v4 as uuid } from 'uuid';
import {StatefulComponent} from '../../../shared/stateful/stateful.component';
import {PncLotNumberInputState} from './pnc-lot-number-input.state';
import {ValidatorFn} from '@angular/forms';
import {PartNumberChangeAdditionalFullLotNumberDetailModel} from '../../models/additional-full-lot-number-detail/part-number-change-additional-full-lot-number-detail.model';
import {PncSeveralFullLotNumberModalComponent} from './pnc-several-full-lot-number-modal/pnc-several-full-lot-number-modal.component';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {PncFullLotNumberChipModel} from './pnc-several-full-lot-number-modal/pnc-full-lot-number-chip/models/pnc-full-lot-number-chip.model';

@Component({
    selector: 'app-pnc-lot-number-input',
    templateUrl: './pnc-lot-number-input.component.html',
    styleUrls: ['./pnc-lot-number-input.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PncLotNumberInputComponent extends StatefulComponent<PncLotNumberInputState> implements OnInit {
    /* Component general styling inputs*/
    @Input() isDetailMode: boolean;
    @Input() isSolvedStatus: boolean;
    @Input() isSolutionAccepted: boolean;
    @Input() isViewMode: boolean;
    /* Full LOT number field inputs */
    @Input() fullLotNumber: string;
    @Input() fullLotNumberValidators: ValidatorFn[];
    /* Quantity field inputs */
    @Input() quantity: number;
    @Input() quantityValidators: ValidatorFn[];

    /* Control the additional full lot numbers */
    @Input() additionalFullLotNumbers: PartNumberChangeAdditionalFullLotNumberDetailModel[];
    @Input() maxNumberOfAdditional: number;
    @Output() newLotNumbers: EventEmitter<PncLotNumberInputState> = new EventEmitter<PncLotNumberInputState>();

    constructor(private modalService: NgbModal) {
        super();
    }

    get canAddSeveral(): boolean {
        let filledCounter = this.isFieldEmpty(this.state?.fullLotNumber?.fullLotNumber) ? 0 : 1;
        filledCounter += this.state?.additionalFullLotNumbers?.filter((elem) => !this.isFieldEmpty(elem.fullLotNumber))?.length ?? 0;
        return (this.maxNumberOfAdditional + 1) > filledCounter && !this.isViewMode;
    }

    get canAddAdditional(): boolean {
        return this.maxNumberOfAdditional > (this.state?.additionalFullLotNumbers ?? []).length && !this.isViewMode;
    }

    get addSeveralTooltipMessage(): string {
        const maxNumberOfAdditional = this.maxNumberOfAdditional ?? 0;
        return `You can't add more than ` + (maxNumberOfAdditional + 1) + ' Full LOT numbers.';
    }

    get addAdditionalTooltipMessage(): string {
        return `You can't add more than ` + this.maxNumberOfAdditional + ' additional Full LOT numbers.';
    }

    ngOnInit(): void {
        this.storeState({
            fullLotNumber: {fullLotNumber: this.fullLotNumber, quantity: this.quantity, isValid: true, id: uuid()},
            additionalFullLotNumbers: this.additionalFullLotNumbers?.map((elem) => ({
                fullLotNumber: elem.fullLotNumber,
                quantity: elem.quantity,
                isValid: true,
                id: uuid()
            }))
        });
    }

    trackByItem = (index: number, item: PartNumberChangesAdditionalFullLotNumberModel): string => item.id;

    newLotNumberChange(event: PartNumberChangesAdditionalFullLotNumberModel): void {
        this.storeState({
            fullLotNumber: {
                ...event
            }
        });
        this.newLotNumbers.emit(this.state);
    }

    addAdditionalLotNumber(): void {
        this.storeState({
            additionalFullLotNumbers: [
                ...this.state.additionalFullLotNumbers ?? [], {
                    fullLotNumber: null,
                    quantity: null,
                    isValid: null,
                    id: uuid()
                }
            ]
        });
    }

    additionalLotNumberChange(event: PartNumberChangesAdditionalFullLotNumberModel): void {
        this.storeState({
            additionalFullLotNumbers: this.state.additionalFullLotNumbers?.map((value) => {
                if (event.id === value.id) {
                    return event;
                } else {
                    return value;
                }
            })
        });
        this.newLotNumbers.emit(this.state);
    }

    addAdditionalSeveralFullLotNumber(): void {
        const modalInstance: PncSeveralFullLotNumberModalComponent = this.modalService.open(
                PncSeveralFullLotNumberModalComponent).componentInstance;
        // Consider only the populated Full LOT No.
        modalInstance.savedFullLotNumbersLength = this.state.additionalFullLotNumbers
                ?.filter((additional) => !this.isFieldEmpty(additional.fullLotNumber)).length ?? 0;
        modalInstance.additionalFullLotNumbersMaxLimit = this.maxNumberOfAdditional;
        // If the first Full LOT No. is empty +1 is added
        modalInstance.fullLotNumbersMaxLimit = this.maxNumberOfAdditional
                + (this.isFieldEmpty(this.state.fullLotNumber.fullLotNumber) ? 1 : 0);

        modalInstance.primaryEvent.subscribe((fullLotNumbers: string[]) => {
            const fullLotNumbersCopy = [...fullLotNumbers];
            if (fullLotNumbers.length && this.addFullLotNumberToFirstPosition([...fullLotNumbers].shift())) {
                fullLotNumbersCopy?.shift();
            }

            // Try to fit the new Full LOT No. into empty positions
            this.storeState({
                additionalFullLotNumbers: this.state.additionalFullLotNumbers?.map((additional) => {
                    if (this.isFieldEmpty(additional.fullLotNumber) && fullLotNumbersCopy?.length) {
                        return {
                            ...additional,
                            fullLotNumber: fullLotNumbersCopy?.shift(),
                            isValid: true
                        };
                    }
                    return additional;
                })
            });
            // If there are remaining Full LOT No. concat them into the existing additional
            if (fullLotNumbersCopy?.length) {
                this.storeState({
                    additionalFullLotNumbers: (this.state.additionalFullLotNumbers ?? []).concat(fullLotNumbersCopy.map((elem) => ({
                        id: uuid(),
                        fullLotNumber: elem,
                        quantity: null,
                        isValid: true
                    })))
                });
            }
        });
    }

    removeAdditionalLotNumbers(event: PartNumberChangesAdditionalFullLotNumberModel): void {
        this.storeState({
            additionalFullLotNumbers: this.state.additionalFullLotNumbers?.filter((value) => value.id !== event.id)
        });
        this.newLotNumbers.emit(this.state);
    }

    private addFullLotNumberToFirstPosition(fullLotNumber: string): boolean {
        let fieldEmpty = false;
        if (this.isFieldEmpty(this.state.fullLotNumber.fullLotNumber) && fullLotNumber) {
            this.newLotNumberChange({
                ...this.state.fullLotNumber,
                fullLotNumber: fullLotNumber
            });
            fieldEmpty = true;
        }
        return fieldEmpty;
    }

    private isFieldEmpty(text: string): boolean {
        return !!(text == null || text === '');
    }
}
